/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { useState, useEffect } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import type {
  StripeCardElementOptions,
  StripeCardElementChangeEvent,
  PaymentMethodResult,
} from "@stripe/stripe-js";
import { useAppDispatch } from "@/hooks";
import { setIsApiLoading } from "@/store/appSlice";

interface UpdateStripePaymentFormProps {
  onSubmit: (paymentMethodId: string) => void;
  isLoading?: boolean;
  error?: string;
}

const CARD_ELEMENT_OPTIONS: StripeCardElementOptions = {
  style: {
    base: {
      color: "#32325d",
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
  hidePostalCode: true,
};

const UpdateStripePaymentForm = ({
  onSubmit,
  isLoading = false,
  error: externalError,
}: UpdateStripePaymentFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useAppDispatch();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (externalError) {
      setError(externalError);
    }
  }, [externalError]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    // Type assertion after null check
    const stripeInstance = stripe;
    const elementsInstance = elements;

    const cardElement = elementsInstance.getElement(CardElement);
    if (!cardElement) {
      setError("カード情報を入力してください");
      return;
    }

    setIsProcessing(true);
    setError(null);
    dispatch(setIsApiLoading(true));

    // Create payment method
    stripeInstance
      .createPaymentMethod({
        type: "card",
        card: cardElement,
      })
      .then((result: PaymentMethodResult) => {
        if (result.error) {
          // Convert Stripe error to Japanese
          let errorMessage = "支払い方法の作成に失敗しました";
          const errorMsg = result.error.message ?? "";
          if (errorMsg.includes("declined")) {
            errorMessage = "カードが拒否されました";
          } else if (errorMsg.includes("insufficient")) {
            errorMessage = "残高が不足しています";
          } else if (errorMsg.includes("expired")) {
            errorMessage = "カードの有効期限が切れています";
          } else if (errorMsg.includes("incorrect")) {
            errorMessage = "カード情報が正しくありません";
          }
          setError(errorMessage);
          setIsProcessing(false);
          dispatch(setIsApiLoading(false));
          return;
        }

        if (!result.paymentMethod) {
          setError("支払い方法の作成に失敗しました");
          setIsProcessing(false);
          dispatch(setIsApiLoading(false));
          return;
        }

        // Call onSubmit with payment method ID
        // Reset loading state - parent component will set it again for API call
        setIsProcessing(false);
        dispatch(setIsApiLoading(false));
        onSubmit(result.paymentMethod.id);
      })
      .catch((err: unknown) => {
        console.error("Payment form error:", err);
        setError("予期しないエラーが発生しました");
        setIsProcessing(false);
        dispatch(setIsApiLoading(false));
      });
  };

  const handleCardChange = (event: StripeCardElementChangeEvent) => {
    if (event.error) {
      const errorMessage = event.error.message ?? "カード情報が正しくありません";
      setError(errorMessage);
    } else {
      setError(null);
    }
  };

  const displayError = externalError ?? error;

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="mb-5">
        <label className="block text-text-main font-sans text-base font-medium leading-[22px] tracking-[0.42px] mb-2.5">
          クレジットカード情報
        </label>
        <div className="w-full rounded-md border border-transparent bg-surface-200 px-4 py-3 text-base text-text-main transition focus-within:bg-surface-100 focus-within:outline-none focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-1">
          <CardElement options={CARD_ELEMENT_OPTIONS} onChange={handleCardChange} />
        </div>
        {displayError && <p className="mt-2 text-sm text-red-500">{displayError}</p>}
      </div>

      <div className="w-full">
        <button
          type="submit"
          disabled={!stripe || isProcessing || isLoading}
          className="mt-4 mx-auto flex w-[80%] items-center justify-center rounded-full bg-gradient-to-r from-gradient-start to-gradient-end px-6 py-3 font-sans text-lg font-semibold text-white shadow-md transition hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          変更する
        </button>
      </div>
    </form>
  );
};

export default UpdateStripePaymentForm;
